// ============================================================
// ConsentInline.tsx — B3c: bill email consent recorded INSIDE the send window
// (bills and credit notes — SendBillModal), never a pop-up on a pop-up.
//   • The amber box: a short message, then (underneath, left-aligned) an
//     amber-outlined "+ Record consent" and a "Send by post instead" link.
//   • "Record consent" expands a small form in the box: email (the client's
//     CURRENT email when it differs from the consented one — said so), the
//     date (Casevo's DateInput; not in the future) and how they agreed.
//   • An address Casevo would still refuse (anything but the client's current
//     email) is explained BEFORE saving, and Save stays off.
//   • Saved → onSaved(state): the send window turns the box green, puts the
//     address in To and re-checks consent at once (no refresh).
// Same server rules + audit as the client's "Bills by email" card
// (PUT /api/clients/:id/bill-email-consent).
// (Batch 2) The amber box is the shared ActionBox (message first, real buttons
// under it). It is NOT a live region any more (it wrapped a whole form): only
// the save error / the "would still be refused" line speak. Field ids come
// from useId (two boxes never clash) and each error is tied to its field with
// aria-describedby. The wording names the document being sent ("bills",
// "credit notes", "statements") instead of always saying "bills".
// (Audit fix batch 2, 31 Oct — the new action-box design) Closed: the notice
// "Consent needed to email this bill" with "Send by post instead" (left) and
// "Record consent" (right). Recording: the notice shrinks to one line
// "· recording…" and a clean white edit card opens beneath it — email · date ·
// how agreed on ONE row, Save consent / Cancel at its foot, "Kept on the client
// record." on the right; focus moves into it and back to "Record consent" on
// Cancel. (M8) The "would still be refused" line is linked to the email field
// and announced once (on leaving the field / on Save), not on every keystroke;
// the date's error is linked to the date boxes.
// ============================================================
import React, { useEffect, useId, useRef, useState } from 'react'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import ActionBox from '../shared/ActionBox'
import ConsentEditCard from './ConsentEditCard'
import { recordBillEmailConsentApi, type BillEmailConsentState } from '../../api/casevoApi'
import { INPUT, NOTE, errorText } from './billingUi'
import { CONSENT_METHOD_OPTIONS } from '../clientInfo/BillEmailConsentCard'
import { londonToday } from '../../utils/civilDate'
import { isValidEmail } from '../../utils/contactFormat'

type Field = 'email' | 'date' | 'method'
// "bills" → "bill", "credit notes" → "credit note", "statements" → "statement".
const singular = (docs: string): string => docs.replace(/s$/, '')
// The shared email rule (utils/contactFormat).
const EMAIL = { test: isValidEmail }
const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()

export interface ConsentFacts {
  consentEmail: string | null
  clientEmail: string | null
  consentChanged: boolean
}
// The short blocked message (pure; exported for tests).
export const consentBlockMessage = (f: ConsentFacts, to: string[]): string => {
  if (f.consentChanged && f.consentEmail && f.clientEmail) return `Consent was given for ${f.consentEmail}, not ${f.clientEmail}.`
  if (f.consentEmail && to.length && to.some((t) => norm(t) !== norm(f.consentEmail))) return `Consent was given for ${f.consentEmail}, not ${to.join(', ')}.`
  return 'No email consent for this address.'
}
// The form's starting email: the client's CURRENT email when it differs from
// the consented one (flagged), else the To address, else the client's email.
export const consentFormStart = (f: ConsentFacts, to: string[]): { email: string; isCurrent: boolean } => {
  if (f.consentEmail && f.clientEmail && norm(f.consentEmail) !== norm(f.clientEmail)) return { email: f.clientEmail, isCurrent: true }
  return { email: to[0] ?? f.clientEmail ?? '', isCurrent: false }
}
// Casevo emails bills only to the client's current address (backend
// lib/billDelivery emailSendAllowed) — consent for any other one is refused
// at send time. Said BEFORE saving (null = fine). `docs` = what is being
// sent, plural ("bills", "credit notes", "statements").
export const consentStillRefused = (email: string, clientEmail: string | null, noun = 'the client', docs = 'bills'): string | null =>
  clientEmail && EMAIL.test(email.trim()) && norm(email) !== norm(clientEmail)
    ? `Casevo only emails ${docs} to ${noun}’s current email address (${clientEmail}), so consent for ${email.trim()} wouldn’t let you send. Record consent for ${clientEmail}, or change ${noun === 'the client' ? 'the client’s email on their details' : `${noun}’s email in Client details`} first.`
    : null
// The form's own checks, mirroring the server's (it re-checks on save).
export const consentFormErrors = (v: { email: string; date: string | null; method: string }, today: string, noun = 'the client'): Partial<Record<Field, string>> => ({
  ...(EMAIL.test(v.email.trim()) ? {} : { email: `Enter the exact email address ${noun} agreed to.` }),
  ...(!v.date ? { date: `Enter the date ${noun} agreed.` } : v.date > today ? { date: 'The date can’t be in the future.' } : {}),
  ...(v.method ? {} : { method: `Choose how ${noun} agreed.` }),
})

interface Props {
  clientId: string | null
  // (B4 follow-up 2) Records the consent for someone other than the client
  // (a child's / protected party's litigation friend) — replaces clientId's.
  recordConsent?: (body: { date: string; email: string; method: string }) => Promise<BillEmailConsentState>
  clientName?: string
  // Who agrees, in the wording ("the client" / "the litigation friend").
  partyNoun?: string
  // (Batch 2) What is being sent, plural ("bills" — the default — / "credit
  // notes" / "statements").
  documentsNoun?: string
  facts: ConsentFacts
  to: string[]
  disabled?: boolean
  onUsePost?: () => void
  onSaved: (s: BillEmailConsentState, email: string, dateIso: string, method: string) => void
}

const ConsentInline: React.FC<Props> = ({ clientId, clientName, facts, to, disabled, onUsePost, onSaved, recordConsent, partyNoun = 'the client', documentsNoun = 'bills' }) => {
  const record = recordConsent ?? (clientId ? (b: { date: string; email: string; method: string }) => recordBillEmailConsentApi(clientId, b) : null)
  const uid = useId()
  const fid = (f: Field): string => `${uid}-${f}`
  const today = londonToday()
  const [open, setOpen] = useState(false)
  const start = consentFormStart(facts, to)
  const [email, setEmail] = useState(start.email)
  const [date, setDate] = useState<string | null>(today)
  const [dateTyping, setDateTyping] = useState(false)
  const [method, setMethod] = useState('')
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [serverErr, setServerErr] = useState<{ field?: Field; text: string } | null>(null)

  const errors = consentFormErrors({ email, date, method }, today, partyNoun)
  const refused = consentStillRefused(email, facts.clientEmail, partyNoun, documentsNoun)
  const shown = (f: Field): string | undefined => (serverErr?.field === f ? serverErr.text : touched ? errors[f] : undefined)
  const describe = (f: Field) => ({
    'aria-invalid': !!shown(f) || undefined,
    'aria-describedby': [shown(f) ? `${fid(f)}-err` : null, f === 'email' && refused ? `${fid(f)}-refused` : null].filter(Boolean).join(' ') || undefined,
  })

  // (M8) The "would still be refused" sentence, announced once (blur / Save).
  const [said, setSaid] = useState('')
  const emailRef = useRef<HTMLInputElement | null>(null)
  // Focus moves into the edit card when it opens; back to "Record consent" on Cancel.
  useEffect(() => { if (open) emailRef.current?.focus() }, [open])
  const openForm = (): void => {
    const s = consentFormStart(facts, to)
    setEmail(s.email); setDate(today); setMethod(''); setTouched(false); setServerErr(null); setSaid(''); setOpen(true)
  }
  const cancel = (): void => {
    setOpen(false)
    window.setTimeout(() => document.getElementById(`${uid}-record`)?.focus(), 0)
  }
  const save = async (): Promise<void> => {
    setTouched(true)
    if (refused) setSaid(refused)
    if (!record || Object.keys(errors).length || refused || dateTyping || busy) return
    setBusy(true); setServerErr(null)
    try {
      const st = await record({ date: date as string, email: email.trim(), method })
      onSaved(st, email.trim(), date as string, method)
    } catch (e) {
      const field = (e as { response?: { data?: { field?: string } } })?.response?.data?.field
      setServerErr({ field: field === 'email' || field === 'date' || field === 'method' ? field : undefined, text: errorText(e, 'Couldn’t record the consent.') })
    } finally { setBusy(false) }
  }

  const title = `Consent needed to email this ${singular(documentsNoun)}`
  const who = clientName?.trim() || (partyNoun === 'the client' ? 'The client' : 'The litigation friend')
  return (
    <div className="flex flex-col gap-[10px]" data-consent-inline="">
      {/* (Audit fix batch 2) Closed: the notice, with "Send by post instead"
          (left) and "Record consent" (right). Recording: the notice shrinks to
          one line and a clean edit card opens beneath it. */}
      {!open ? (
        <ActionBox tone="warn" title={title} message={consentBlockMessage(facts, to)}
          actions={[
            ...(onUsePost ? [{ label: 'Send by post instead', kind: 'link' as const, onClick: onUsePost, disabled }] : []),
            ...(record ? [{ label: 'Record consent', kind: 'primary' as const, onClick: openForm, disabled, id: `${uid}-record` }] : []),
          ]} />
      ) : (
        <ActionBox tone="warn" variant="slim" title={title} status="· recording…" />
      )}
      {open && (
        <ConsentEditCard context={<>{who} · {documentsNoun} by email</>} busy={busy} saveDisabled={!!refused} onSave={() => { void save() }} onCancel={cancel}
          footNote={partyNoun === 'the client' ? 'Kept on the client record.' : 'Kept with the litigation friend’s details.'}
          error={serverErr && !serverErr.field ? serverErr.text : null}>
            <FormField label="Email address agreed" required variant="info" htmlFor={fid('email')}>
              <input id={fid('email')} ref={emailRef} type="email" className={INPUT} value={email} maxLength={200} disabled={busy} placeholder="name@example.com"
                onChange={(e) => { setEmail(e.target.value); if (serverErr?.field === 'email') setServerErr(null) }}
                onBlur={() => setSaid(refused ?? '')} {...describe('email')} />
              <span id={`${fid('email')}-err`}><FieldError message={shown('email')} /></span>
              {start.isCurrent && norm(email) === norm(facts.clientEmail) && <p className={`${NOTE} mt-[4px] mb-0`}>This is {clientName?.trim() || 'the client'}’s current email. The earlier consent was for {facts.consentEmail}.</p>}
              {/* (M8) Shown and linked to the field — NOT an alert on every
                  keystroke; it is announced once, on leaving the field or on Save. */}
              {refused && <p id={`${fid('email')}-refused`} className="text-[12px] text-casevo-danger mt-[4px] mb-0">{refused}</p>}
            </FormField>
            <FormField label="Date agreed" required variant="info">
              <DateInput value={date} onChange={(v) => { setDate(v); if (serverErr?.field === 'date') setServerErr(null) }} ariaLabel="Date agreed" onValidityChange={(ok) => setDateTyping(!ok)}
                describedBy={shown('date') ? `${fid('date')}-err` : undefined} invalid={!!shown('date')} />
              <span id={`${fid('date')}-err`}><FieldError message={shown('date')} /></span>
            </FormField>
            <FormField label={`How ${partyNoun} agreed`} required variant="info" htmlFor={fid('method')}>
              <select id={fid('method')} className={INPUT} value={method} disabled={busy} onChange={(e) => { setMethod(e.target.value); if (serverErr?.field === 'method') setServerErr(null) }} {...describe('method')}>
                <option value="">Choose…</option>
                {CONSENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span id={`${fid('method')}-err`}><FieldError message={shown('method')} /></span>
            </FormField>
        </ConsentEditCard>
      )}
      {open && <span role="status" className="sr-only">{said}</span>}
    </div>
  )
}

export default ConsentInline
